import { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Search, Compass, UserCheck, ListChecks, Gauge, KanbanSquare, Send,
  FileText, Handshake, ShieldCheck, Scale, Sparkles, ChevronRight,
} from "lucide-react";

/**
 * BLOCK 113 ,  Subscriber Help & Guide.
 * Organized around the real path to value (set up -> find -> score ->
 * pipeline -> reach out -> documents -> close), not the nav menu. Every
 * section is subscriber-accessible on all tiers. Contains no restricted
 * surfaces, no internal data-source/vendor names, no server paths, and no
 * credentials.
 */

const TIER = ({ children }) => (
  <span className="inline-block align-middle ml-1.5 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 rounded-sm">
    {children}
  </span>
);

// Each section carries plain-text `keywords` so the search box can filter the
// whole page down to what a subscriber typed.
const SECTIONS = [
  {
    id: "start-here",
    icon: Compass,
    title: "Start here",
    tagline: "The only three things required before you can do anything real.",
    keywords: "start setup onboarding sender profile email provider sender agreement required first begin getting set up identity",
    body: (
      <div className="space-y-4">
        <p className="text-slate-300 leading-relaxed">
          You can browse opportunities and scores the moment you log in. But before you send a
          single message or generate a document, three things need to be in place. Everything else
          in this guide is explained where it actually becomes relevant, so do not feel you have
          to configure the whole platform up front.
        </p>
        <ol className="space-y-3">
          <li className="ae-card p-4 flex gap-3" data-testid="start-step-profile">
            <span className="font-heading text-emerald-400 text-lg leading-none">1</span>
            <div>
              <div className="font-medium text-slate-100">Complete your sender profile</div>
              <p className="text-sm text-slate-400 mt-1">
                Go to <Link to="/app/settings" className="text-emerald-300 hover:underline">Settings</Link>{" "}
                and fill in your name, title, sending email, and physical postal address. This is who
                every outbound message and document comes from, it is your identity, never the
                platform&apos;s. A complete postal address is legally required on outreach, so sends
                are blocked until this is filled in.
              </p>
            </div>
          </li>
          <li className="ae-card p-4 flex gap-3" data-testid="start-step-email">
            <span className="font-heading text-emerald-400 text-lg leading-none">2</span>
            <div>
              <div className="font-medium text-slate-100">Connect one email provider</div>
              <p className="text-sm text-slate-400 mt-1">
                In <Link to="/app/settings" className="text-emerald-300 hover:underline">Settings → Integrations</Link>,
                connect your own email-sending account by pasting its API key. You only need one to
                start. When you paste a valid key we validate it and finish the behind-the-scenes
                setup for you automatically, there is nothing to copy back and forth. See{" "}
                <a href="#reaching-out" className="text-emerald-300 hover:underline">Reaching out</a>{" "}
                for exactly how this works now.
              </p>
            </div>
          </li>
          <li className="ae-card p-4 flex gap-3" data-testid="start-step-agreement">
            <span className="font-heading text-emerald-400 text-lg leading-none">3</span>
            <div>
              <div className="font-medium text-slate-100">Read &amp; accept the Sender Agreement</div>
              <p className="text-sm text-slate-400 mt-1">
                Also in Settings → Integrations. This is a real legal acknowledgement that you are the
                sender of everything you dispatch, not a formality to click past. Read the{" "}
                <a href="/sender-agreement" target="_blank" rel="noopener noreferrer" className="text-emerald-300 hover:underline">
                  Sender Agreement
                </a>{" "}
                in full, the short version is in{" "}
                <a href="#legal" className="text-emerald-300 hover:underline">Your legal responsibilities</a>.
              </p>
            </div>
          </li>
        </ol>
        <p className="text-sm text-slate-500">
          Optional connectors (e-signature, social posting) are deliberately kept out of this first
          run. You will be prompted to add them the first time you actually need them, so there is no
          need to set them up now. If you prefer to configure everything up front, you can also set
          them up at any time from Settings.
        </p>
      </div>
    ),
  },
  {
    id: "finding",
    icon: ListChecks,
    title: "Finding opportunities",
    tagline: "Where deals come from and how to add your own.",
    keywords: "find opportunities leads search filters add opportunity intake public records import businesses multifamily land discover",
    body: (
      <div className="space-y-4">
        <p className="text-slate-300 leading-relaxed">
          The <Link to="/app/opportunities" className="text-emerald-300 hover:underline">Opportunities</Link>{" "}
          list is your working surface. Each row is a potential deal, a business, a multifamily
          property, or a piece of land, assembled from public records and licensed data sources and
          scored for you. Use the filters at the top to narrow by type, location, score tier, or
          freshness so you can focus on the strongest signals first.
        </p>
        <ul className="space-y-2 text-sm text-slate-400">
          <li className="flex gap-2"><ChevronRight className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
            <span><span className="text-slate-200">Opportunities</span>, browse and filter everything scored and ready.</span></li>
          <li className="flex gap-2"><ChevronRight className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
            <span><span className="text-slate-200">Leads</span>, the people behind the opportunities and their contact details, once known.</span></li>
          <li className="flex gap-2"><ChevronRight className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
            <span><Link to="/app/intake" className="text-emerald-300 hover:underline">Add Opportunity</Link>, bring in a deal you already know about; it gets scored the same way.</span></li>
          <li className="flex gap-2"><ChevronRight className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
            <span><span className="text-slate-200">Bulk Import</span> <TIER>Business</TIER> lets you upload a spreadsheet of many opportunities at once.</span></li>
        </ul>
        <p className="text-sm text-slate-500">
          Click any row to open its detail view, where the full score breakdown, owner intelligence,
          documents, and outreach live together.
        </p>
      </div>
    ),
  },
  {
    id: "score",
    icon: Gauge,
    title: "Understanding the CF Readiness score",
    tagline: "What the number means and why some deals show less data.",
    keywords: "cf readiness score dimensions motivation life events receptivity viability owner access freshness confidence first pass fully scored limited data close probability tier hot strong qualified watchlist creative finance",
    body: (
      <div className="space-y-4">
        <p className="text-slate-300 leading-relaxed">
          Every opportunity gets a <span className="text-slate-100">CF Readiness Score</span> out of 100.
          &ldquo;CF&rdquo; stands for creative finance, the score estimates how ready a deal is for a
          seller-financed or creatively-structured offer, based on how motivated the owner appears and
          how workable the numbers are. It is a transparent, rules-based number: every point traces
          back to a specific, visible reason, there is no black box.
        </p>
        <div className="ae-card p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-3">The six dimensions</div>
          <div className="space-y-2 text-sm">
            {[
              ["Seller Motivation", "How strongly the owner looks ready to move on now."],
              ["Life Events", "Changes around the owner, ownership tenure, and timing signals."],
              ["CF Receptivity", "How open the situation looks to creative or seller-financed terms."],
              ["Deal Viability", "Whether the underlying numbers support a workable structure."],
              ["Owner Access", "How reachable the owner is with the contact details on hand."],
              ["Lead Freshness", "How recent the triggering signal is; a newer signal counts for more."],
            ].map(([name, desc]) => (
              <div key={name} className="border-b border-white/5 pb-2 last:border-0 last:pb-0">
                <div className="text-slate-200">{name}</div>
                <div className="text-xs text-slate-500">{desc}</div>
              </div>
            ))}
          </div>
        </div>
        <p className="text-slate-300 leading-relaxed">
          On top of the six dimensions, a deal can earn <span className="text-slate-100">compound-signal
          bonuses</span> when several strong signals line up at once, and it carries an{" "}
          <span className="text-slate-100">estimated close probability</span> and a{" "}
          <span className="text-slate-100">score tier</span> (from Watchlist up through Qualified,
          Strong, and Hot) so you can triage at a glance.
        </p>
        <div className="ae-card p-4 border-white/10">
          <div className="font-medium text-slate-100 mb-1">First-pass vs. fully scored, and why some deals show limited data</div>
          <p className="text-sm text-slate-400 leading-relaxed">
            A brand-new opportunity starts as <span className="text-slate-200">first-pass</span>:
            it has been discovered from public property records, but the signals that actually
            drive readiness &mdash; owner motivation, life events, how reachable the owner is, and
            how workable the numbers are &mdash; have not been checked yet. Because almost none of
            those inputs are known at this stage, a first-pass opportunity is deliberately given a{" "}
            <span className="text-slate-200">low, preliminary readiness score</span>. That low number
            means &ldquo;not evaluated yet,&rdquo; <span className="text-slate-200">not</span>
            &ldquo;weak deal&rdquo; &mdash; it simply keeps unevaluated rows from sitting above deals
            that have genuinely earned a high score. First-pass opportunities are still fully
            browsable and are ordered sensibly by property type and size so you can find them.
            Once the deeper data is gathered, the opportunity becomes{" "}
            <span className="text-slate-200">fully scored</span> and its readiness number is
            recalculated across all six dimensions to reflect what is really there. Each opportunity
            also shows a <span className="text-slate-200">confidence</span> indicator &mdash; how many
            of the useful data points we could actually find. You can trigger a fresh recalculation
            from an opportunity&apos;s detail view at any time.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: "pipeline",
    icon: KanbanSquare,
    title: "Building a pipeline",
    tagline: "Moving the best opportunities toward a deal.",
    keywords: "pipeline stages board kanban move advance tasks track progress deal flow",
    body: (
      <div className="space-y-4">
        <p className="text-slate-300 leading-relaxed">
          Once you have found opportunities worth pursuing, work them in{" "}
          <Link to="/app/pipeline" className="text-emerald-300 hover:underline">Pipeline</Link>, a
          board of stages that runs from a freshly discovered lead through to a signed and closed deal.
          Some stages advance on their own as things happen; others wait for you to move them
          deliberately. Either way, a deal only ever moves forward, never backward.
        </p>
        <div className="ae-card p-4">
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2">Stages that advance on their own</div>
          <ul className="space-y-2 text-sm text-slate-400">
            <li className="flex gap-2"><ChevronRight className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
              <span>A lead becomes <span className="text-slate-200">Scored</span> automatically the moment its readiness score is calculated.</span></li>
            <li className="flex gap-2"><ChevronRight className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
              <span>It moves to <span className="text-slate-200">Outreach Sent</span> when your first outreach actually goes out. A strong score marks a lead as ready to contact, and it advances the moment you approve and the message sends.</span></li>
            <li className="flex gap-2"><ChevronRight className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
              <span>It moves to <span className="text-slate-200">Engaged</span> when the owner replies with interest.</span></li>
            <li className="flex gap-2"><ChevronRight className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
              <span>It moves to <span className="text-slate-200">Under LOI</span> when a letter of intent is sent for signature, and to <span className="text-slate-200">Contract Signed</span> when the purchase agreement is signed.</span></li>
            <li className="flex gap-2"><ChevronRight className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
              <span>It is marked <span className="text-slate-200">Dead</span> automatically if it sits in Outreach Sent for 30 days after your first outreach with no reply moving it forward. This is a stage change, not a deletion, the opportunity and all of its history are kept.</span></li>
          </ul>
        </div>
        <div className="ae-card p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">Stages you advance yourself</div>
          <ul className="space-y-2 text-sm text-slate-400">
            <li className="flex gap-2"><ChevronRight className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
              <span>Move a deal into the <span className="text-slate-200">Offer</span> stage once you have negotiated terms.</span></li>
            <li className="flex gap-2"><ChevronRight className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
              <span>Move it into <span className="text-slate-200">Due Diligence</span> when you begin verifying the deal.</span></li>
            <li className="flex gap-2"><ChevronRight className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
              <span>Mark it <span className="text-slate-200">Closed</span> yourself, recording the close date and final price.</span></li>
            <li className="flex gap-2"><ChevronRight className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
              <span><span className="text-slate-200">Revive a Dead opportunity.</span> If an opportunity was marked Dead (by you or by the 30-day rule), you can revive it back to Scored from its detail page to restart communication. Reviving does not send anything by itself, it simply puts the opportunity back on your active board so the normal outreach controls apply again. When you revive, we show how many of your 3 emails-per-90-days for that contact remain, so you know whether a fresh send is possible yet. One firm exception: a contact who opted out or unsubscribed can never be revived or contacted again, no matter how much time passes.</span></li>
          </ul>
        </div>
        <p className="text-sm text-slate-500">
          Each card keeps its score as it advances. Use{" "}
          <Link to="/app/tasks" className="text-emerald-300 hover:underline">Tasks</Link> to keep
          follow-ups from slipping, <Link to="/app/alerts" className="text-emerald-300 hover:underline">Alerts</Link>{" "}
          to get notified when a watched deal heats up, and{" "}
          <Link to="/app/reports" className="text-emerald-300 hover:underline">Reports</Link> to see how
          your pipeline is trending over time. Moving a deal into outreach and the stages beyond it
          requires a paid plan.
        </p>
      </div>
    ),
  },
  {
    id: "reaching-out",
    icon: Send,
    title: "Reaching out",
    tagline: "Sending from your identity, on your account, within the rules.",
    keywords: "outreach email send campaign sequence contact hours override attestation tcpa can-spam unsubscribe provider queue ready to contact api key",
    body: (
      <div className="space-y-4">
        <p className="text-slate-300 leading-relaxed">
          Outreach <TIER>Pro</TIER> always sends from <span className="text-slate-100">your own connected
          email account</span>, never from the platform. That is why connecting a provider is part of
          the start-here setup.
        </p>
        <div className="ae-card p-4">
          <div className="font-medium text-slate-100 mb-1">How connecting an email provider works now</div>
          <p className="text-sm text-slate-400 leading-relaxed">
            In Settings → Integrations, paste the API key from your email-sending account and click
            Connect. We immediately check the key against your account and tell you exactly what, if
            anything, still needs attention, for example a sending domain that is not verified yet,
            or open and click tracking that is switched off on your side. The delivery tracking that
            surfaces opens, clicks, and bounces back into your history is wired up automatically for
            you the moment the key is valid; there are no URLs to copy or settings to paste back. If
            you connect more than one provider, you choose which one your sends go through.
          </p>
        </div>
        <div className="ae-card p-4 border-amber-500/30 bg-amber-500/[0.04]">
          <div className="font-medium text-amber-200 mb-1">Contact-hour restrictions and the override</div>
          <p className="text-sm text-slate-400 leading-relaxed">
            Some sends will be blocked because the recipient is currently outside the legally permitted
            contact window for their state (roughly local daytime hours). This is a legal protection,
            not a glitch. You can proceed anyway only by completing an{" "}
            <span className="text-amber-200">override</span>, which requires ticking an attestation and
            writing a justification. That override is a <span className="text-slate-100">legal
            attestation</span> that you have a lawful basis to contact this person outside the standard
            window (such as prior express consent or an established business relationship), and that you
            take responsibility for the send. It is recorded. Treat it as your signature, not a
            convenience button to skip a warning.
          </p>
        </div>
        <p className="text-sm text-slate-400 leading-relaxed">
          Every email we help you send carries the legally required pieces automatically, a one-click
          unsubscribe and your physical postal address, and honors opt-outs. Keeping your sender
          profile complete is what makes that possible. More on your obligations under{" "}
          <a href="#legal" className="text-emerald-300 hover:underline">Your legal responsibilities</a>.
        </p>
      </div>
    ),
  },
  {
    id: "documents",
    icon: FileText,
    title: "Generating documents",
    tagline: "Drafts from your details, reviewed by you (and your attorney).",
    keywords: "documents assistant loi letter of intent term sheet proposal template e-signature signature generate pdf docx attorney review",
    body: (
      <div className="space-y-4">
        <p className="text-slate-300 leading-relaxed">
          The <Link to="/app/assistant" className="text-emerald-300 hover:underline">AI Assistant</Link>{" "}
          <TIER>Pro</TIER> drafts letters of intent, term sheets, and proposals from an opportunity&apos;s
          details and your sender profile. You can preview and download them, and send a signable
          version through your own connected e-signature account when you are ready.
        </p>
        <div className="ae-card p-4 border-amber-500/30 bg-amber-500/[0.04]">
          <div className="font-medium text-amber-200 mb-1">Generated documents are templates</div>
          <p className="text-sm text-slate-400 leading-relaxed">
            What the Assistant produces is a starting template populated with your inputs, not legal
            advice and not a substitute for an attorney. Read every document before it goes out. Some
            states require a licensed real estate attorney to review and conduct the closing, and the
            platform will flag those, but the responsibility to have your documents reviewed is always
            yours. See <a href="#closing" className="text-emerald-300 hover:underline">Closing</a>.
          </p>
        </div>
        <p className="text-sm text-slate-500">
          The first time you send a document for signature, you will be prompted to connect your
          e-signature account. That connector is deferred until you actually need it, so it does not
          clutter your initial setup. If you would rather set it up ahead of time, you can also connect
          it at any time from Settings.
        </p>
      </div>
    ),
  },
  {
    id: "closing",
    icon: Handshake,
    title: "Closing",
    tagline: "Getting to signed, and celebrating it.",
    keywords: "closing signed deal state requirements attorney closing states disclosures social post celebration contract signed",
    body: (
      <div className="space-y-4">
        <p className="text-slate-300 leading-relaxed">
          As a deal nears the finish line, the{" "}
          <Link to="/app/documents/library" className="text-emerald-300 hover:underline">State Requirements</Link>{" "}
          <TIER>Pro</TIER> reference tells you what a given state expects, including which states are
          attorney-closing states where a licensed attorney must handle the closing. Line your paperwork
          and review up against that before you execute anything.
        </p>
        <p className="text-slate-300 leading-relaxed">
          When a deal is marked as signed, you can optionally publish a closed-deal celebration to your
          own social accounts. Like e-signature, that social connector is deferred: you will be offered
          the chance to connect it the first time you go to post. If you prefer, you can also set it up
          at any time from Settings.
        </p>
      </div>
    ),
  },
  {
    id: "tiers",
    icon: Sparkles,
    title: "Plans & what each unlocks",
    tagline: "So you never follow steps for something your plan cannot reach.",
    keywords: "tiers plans free pro business pricing upgrade gate locked features what included billing",
    body: (
      <div className="space-y-4">
        <p className="text-slate-300 leading-relaxed">
          Everyone, including the free plan, can find opportunities, read every score in full, build a
          pipeline, add opportunities, use tasks and alerts, and see reports. A few capabilities are
          reserved for paid plans, and they are marked with a badge in the sidebar so you always
          know before you start:
        </p>
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="ae-card p-4">
            <div className="font-heading text-lg text-slate-100">Free</div>
            <ul className="text-sm text-slate-400 mt-2 space-y-1">
              <li>Browse &amp; filter opportunities</li>
              <li>Full score breakdowns</li>
              <li>Pipeline, tasks &amp; alerts</li>
              <li>Add opportunities &amp; reports</li>
            </ul>
          </div>
          <div className="ae-card p-4 border-emerald-500/30">
            <div className="font-heading text-lg text-emerald-300">Pro</div>
            <ul className="text-sm text-slate-400 mt-2 space-y-1">
              <li>Everything in Free</li>
              <li>Outreach &amp; email sending</li>
              <li>AI Assistant documents</li>
              <li>State Requirements reference</li>
            </ul>
          </div>
          <div className="ae-card p-4 border-emerald-500/30">
            <div className="font-heading text-lg text-emerald-300">Business</div>
            <ul className="text-sm text-slate-400 mt-2 space-y-1">
              <li>Everything in Pro</li>
              <li>Bulk import of opportunities</li>
              <li>Highest usage limits</li>
            </ul>
          </div>
        </div>
        <p className="text-sm text-slate-500">
          You can compare and change plans on the{" "}
          <Link to="/app/billing" className="text-emerald-300 hover:underline">Billing</Link> page. A
          locked feature in the sidebar links straight to the upgrade that unlocks it.
        </p>
      </div>
    ),
  },
  {
    id: "legal",
    icon: Scale,
    title: "Your legal responsibilities",
    tagline: "Plain-language version of what you are personally accountable for.",
    keywords: "legal sender agreement can-spam tcpa unsubscribe do not call dnc contact hours attestation attorney templates liability compliance obligations",
    body: (
      <div className="space-y-4">
        <p className="text-slate-300 leading-relaxed">
          These are not buried fine print; they are things you are personally liable for. Here it
          is in plain terms; the binding version is the{" "}
          <a href="/sender-agreement" target="_blank" rel="noopener noreferrer" className="text-emerald-300 hover:underline">
            Sender Agreement
          </a>.
        </p>
        <ul className="space-y-2.5 text-sm text-slate-400">
          <li className="flex gap-2"><ShieldCheck className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
            <span><span className="text-slate-200">You are the sender.</span> Every email, document, or
            message you send through the platform is legally yours, sent from your identity on your own
            connected account. The platform never sends outreach as itself on your behalf.</span></li>
          <li className="flex gap-2"><ShieldCheck className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
            <span><span className="text-slate-200">CAN-SPAM basics.</span> Commercial email must have
            honest headers and subject lines, identify itself as an advertisement where required,
            include your real physical postal address, and offer a working one-click unsubscribe that
            you honor promptly. We add the address and unsubscribe for you automatically, which is why
            your sender profile has to be complete.</span></li>
          <li className="flex gap-2"><ShieldCheck className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
            <span><span className="text-slate-200">Contact-hour limits are real law.</span> When a send
            is blocked for being outside the recipient&apos;s permitted contact window, overriding it is
            a legal attestation that you have a lawful basis to contact them anyway. Do not use it
            casually; it is recorded and it is on you.</span></li>
          <li className="flex gap-2"><ShieldCheck className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
            <span><span className="text-slate-200">Documents need your own review.</span> Generated
            documents are templates, not legal advice. Have them reviewed by your own attorney, and
            respect states that require an attorney to conduct the closing.</span></li>
          <li className="flex gap-2"><ShieldCheck className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
            <span><span className="text-slate-200">Keep your own records and counsel.</span> The platform
            gives you tools and an audit trail, but it does not provide legal advice. Retain your own
            attorney for your specific situation.</span></li>
        </ul>
      </div>
    ),
  },
];

const NAV = SECTIONS.map((s) => ({ id: s.id, title: s.title, icon: s.icon }));

export default function Help() {
  const [q, setQ] = useState("");
  const [active, setActive] = useState(SECTIONS[0].id);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return SECTIONS;
    return SECTIONS.filter(
      (s) =>
        s.title.toLowerCase().includes(term) ||
        s.tagline.toLowerCase().includes(term) ||
        s.keywords.toLowerCase().includes(term)
    );
  }, [q]);

  // Highlight the section nearest the top of the viewport as the user scrolls.
  useEffect(() => {
    if (q.trim()) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-20% 0px -70% 0px", threshold: 0 }
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [q]);

  return (
    <div className="space-y-6" data-testid="help-page">
      <header className="space-y-2">
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-100">Help &amp; Guide</h1>
        <p className="text-slate-400 max-w-2xl">
          A walk-through built around how you actually get to a deal, set up, find opportunities,
          understand the score, build a pipeline, reach out, generate documents, and close. Start at
          the top; each step explains the parts of the platform where they matter.
        </p>
        <div className="relative max-w-md">
          <Search className="h-4 w-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="help-search"
            placeholder="Search the guide, e.g. score, contact hours, unsubscribe"
            className="w-full bg-white/[0.03] border border-white/10 rounded-sm pl-9 pr-3 py-2 text-sm text-slate-200 placeholder:text-slate-600 focus:border-emerald-500/40 outline-none"
          />
        </div>
      </header>

      <div className="grid lg:grid-cols-[220px_1fr] gap-8">
        {/* Anchor nav */}
        <aside className="hidden lg:block">
          <nav className="sticky top-6 space-y-1" data-testid="help-toc">
            {NAV.map(({ id, title, icon: Icon }) => (
              <a
                key={id}
                href={`#${id}`}
                data-testid={`help-toc-${id}`}
                className={`flex items-center gap-2.5 px-3 py-2 rounded-sm text-sm transition-colors ${
                  active === id && !q.trim()
                    ? "bg-emerald-500/10 text-emerald-300 border-l-2 border-emerald-500 pl-[10px]"
                    : "text-slate-400 hover:text-slate-100 hover:bg-white/[0.03]"
                }`}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="truncate">{title}</span>
              </a>
            ))}
          </nav>
        </aside>

        {/* Sections */}
        <div className="space-y-10 min-w-0">
          {filtered.length === 0 && (
            <div className="ae-card p-6 text-slate-400" data-testid="help-no-results">
              Nothing matched &ldquo;{q}&rdquo;. Try a simpler word like &ldquo;score&rdquo;,
              &ldquo;email&rdquo;, or &ldquo;legal&rdquo;.
            </div>
          )}
          {filtered.map(({ id, icon: Icon, title, tagline, body }) => (
            <section key={id} id={id} className="scroll-mt-6" data-testid={`help-section-${id}`}>
              <div className="flex items-start gap-3 mb-4">
                <div className="h-9 w-9 rounded-sm bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center shrink-0">
                  <Icon className="h-4.5 w-4.5 text-emerald-300" />
                </div>
                <div>
                  <h2 className="font-heading text-xl tracking-tight text-slate-100">{title}</h2>
                  <p className="text-sm text-slate-500">{tagline}</p>
                </div>
              </div>
              {body}
            </section>
          ))}

          <div className="ae-card p-5 flex items-center justify-between gap-4" data-testid="help-footer-cta">
            <div>
              <div className="font-medium text-slate-100">Ready to set up?</div>
              <p className="text-sm text-slate-400">Head to Settings to complete your sender profile, connect a provider, and accept the Sender Agreement.</p>
            </div>
            <Link to="/app/settings" data-testid="help-go-settings" className="btn-primary text-sm px-4 py-2 inline-flex items-center gap-2 shrink-0">
              <UserCheck className="h-4 w-4" /> Open Settings
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
